import {
  arrow,
  autoUpdate,
  flip,
  FloatingArrow,
  FloatingPortal,
  offset,
  safePolygon,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole,
  useTransitionStyles,
} from '@floating-ui/react';
import { clsx } from 'clsx';
import { useRef, useState } from 'react';
import { IJxtInfoTooltip, JxtInfoTooltipTypeEnum } from './types';

const ARROW_HEIGHT = 7;
const HOVER_DELAY = { open: 150, close: 100 };

const TYPES: Record<JxtInfoTooltipTypeEnum, { icon: string; trigger: string; accent: string }> = {
  [JxtInfoTooltipTypeEnum.Info]: {
    icon: 'circle-info',
    trigger: 'text-neutral-60 hover:text-neutral-100',
    accent: 'text-neutral-90',
  },
  [JxtInfoTooltipTypeEnum.Help]: {
    icon: 'circle-question',
    trigger: 'text-neutral-60 hover:text-neutral-100',
    accent: 'text-neutral-90',
  },
  [JxtInfoTooltipTypeEnum.Tip]: {
    icon: 'lightbulb',
    trigger: 'text-primary-60 hover:text-primary-100',
    accent: 'text-primary-100',
  },
};

/**
 * An explanation behind a small icon (ⓘ, ?, 💡): what a section is, how something works, a tip — a
 * title, a description and maybe a link, in a light card. Not for a cut text (JxtTooltip/JxtTruncatedText).
 * Opens on hover (the pointer can move into it), on keyboard focus and on a tap; Escape or a click
 * outside closes it.
 */
const JxtInfoTooltip = ({
  type = JxtInfoTooltipTypeEnum.Info,
  title,
  description,
  action,
  triggerLabel,
  placement = 'bottom-start',
}: IJxtInfoTooltip) => {
  const [isOpen, setIsOpen] = useState(false);
  const arrowRef = useRef<SVGSVGElement>(null);
  const { icon, trigger, accent } = TYPES[type];
  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement,
    whileElementsMounted: autoUpdate,
    middleware: [offset(ARROW_HEIGHT + 2), flip(), shift({ padding: 8 }), arrow({ element: arrowRef })],
  });
  const { getReferenceProps, getFloatingProps } = useInteractions([
    useHover(context, { delay: HOVER_DELAY, handleClose: safePolygon() }),
    useFocus(context),
    // A tap or a key: the mouse already opened it on hover.
    useClick(context, { ignoreMouse: true }),
    useDismiss(context),
    // With an action, it holds something to reach: a dialog, not a mere description.
    useRole(context, { role: action ? 'dialog' : 'tooltip' }),
  ]);
  const { isMounted, styles } = useTransitionStyles(context, { duration: 120 });

  return (
    <>
      <button
        ref={refs.setReference}
        type="button"
        aria-label={triggerLabel ?? title}
        className={clsx(
          'inline-flex size-5 shrink-0 cursor-help items-center justify-center rounded-full focus-visible:outline-2 focus-visible:outline-primary-100',
          trigger
        )}
        {...getReferenceProps()}
      >
        <i aria-hidden className={clsx('fas text-body-xs', `fa-${icon}`)} />
      </button>
      {isMounted && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={{ ...floatingStyles, ...styles }}
            className="z-[80]"
            {...getFloatingProps()}
          >
            <div className="flex w-72 max-w-[calc(100vw-1rem)] flex-col gap-1 rounded-lg border border-neutral-20 bg-white p-3 text-left shadow-elevation-3">
              {title && (
                <p className="flex items-center gap-2 text-body-s font-semibold text-neutral-140">
                  <i aria-hidden className={clsx('fas text-body-xs', `fa-${icon}`, accent)} />
                  {title}
                </p>
              )}
              <div className="text-body-xs text-neutral-100">{description}</div>
              {action && (
                <button
                  type="button"
                  onClick={() => {
                    setIsOpen(false);
                    action.onClick();
                  }}
                  className="self-start pt-1 text-body-xs font-semibold text-primary-100 hover:text-primary-110 hover:underline"
                >
                  {action.label}
                </button>
              )}
            </div>
            <FloatingArrow
              ref={arrowRef}
              context={context}
              height={ARROW_HEIGHT}
              fill="white"
              stroke="var(--color-neutral-20)"
              strokeWidth={1}
            />
          </div>
        </FloatingPortal>
      )}
    </>
  );
};

export default JxtInfoTooltip;
